import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type { Lead, LeadsSearchParams, SelectedFacetParam } from "common/types";

interface PagingParams {
  order: "asc" | "desc";
  pageSize: number;
  page: number;
}

async function searchLeadsRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  pagingParams: PagingParams,
): Promise<Lead[]> {
  const { page, pageSize, order } = pagingParams;
  const queryParams = new URLSearchParams();
  queryParams.set("start", (page * pageSize).toString());
  queryParams.set("count", pageSize.toString());
  queryParams.set("order", order);

  const { leads } = await post(
    `workspaces/${workspaceId}/leads-search?${queryParams.toString()}`,
    parseLeadsSearchParams(searchParams),
  );
  return leads;
}

export default function useLeads(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  pagingParams: PagingParams,
) {
  const { id: workspaceId, accounts_count: accountsCount } =
    useSelectedWorkspaceContext();

  const { leadsKeys } = getQueryKeys(workspaceId);

  const {
    data: leads,
    isFetching: isFetchingLeads,
    error: leadsError,
    refetch: refetchLeads,
  } = useQuery({
    queryKey: leadsKeys.list(searchParams, pagingParams),
    queryFn: () => searchLeadsRequest(workspaceId, searchParams, pagingParams),
    enabled: accountsCount > 0,
    placeholderData: keepPreviousData, // Keep previous page visible while next page loads, preventing mass unmount/remount of per-cell query observers
    gcTime: 0,
  });

  return {
    leads,
    isFetchingLeads,
    refetchLeads,
    leadsError,
  };
}
